import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { MainLayout } from '@/layouts/MainLayout';
import {
  Container,
  Stack,
  Button,
  Alert,
  ProgressSpinner,
  SimpleSwitch,
  PageHeader,
} from '@/components/ui';
import { BalticAccordionItem } from '@/components/baltic/BalticAccordionItem';
import { BalticSkeleton } from '@/components/baltic/BalticSkeleton';
import { useBalticStore } from '@/store/balticStore';
import { BalticHistoryRequest } from '@/types/baltic.types';

export function BalticPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const routeParam = searchParams.get('route');
  const periodParam = searchParams.get('period') as BalticHistoryRequest['period'] | null;

  const [expandedRoute, setExpandedRoute] = useState<string | null>(routeParam);
  const [hasInitialized, setHasInitialized] = useState(false);

  const {
    routes,
    historyData,
    selectedPeriod,
    showTCE,
    loading,
    error,
    historyLoading,
    fetchRoutes,
    setSelectedRoute,
    setSelectedPeriod,
    setShowTCE,
    refreshData,
  } = useBalticStore();

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  // Set period from URL on mount
  useEffect(() => {
    if (periodParam && ['daily', 'weekly', 'monthly', 'yearly'].includes(periodParam)) {
      setSelectedPeriod(periodParam);
    }
  }, [periodParam, setSelectedPeriod]);

  // Auto-expand first route when data loads if no route in URL (only on initial load)
  useEffect(() => {
    if (routes.length > 0 && !hasInitialized) {
      setHasInitialized(true);
      if (!routeParam) {
        const firstRoute = routes[0];
        setExpandedRoute(firstRoute.route);
        setSelectedRoute(firstRoute); // This will trigger history fetch via store
        setSearchParams({ route: firstRoute.route, period: selectedPeriod });
      } else {
        const route = routes.find((r) => r.route === routeParam);
        if (route) {
          setExpandedRoute(route.route);
          setSelectedRoute(route); // This will trigger history fetch via store
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes.length, hasInitialized]);

  const handlePeriodChange = (period: BalticHistoryRequest['period']) => {
    setSelectedPeriod(period);
    if (expandedRoute) {
      setSearchParams({ route: expandedRoute, period });
    }
  };

  const periods = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' },
  ];

  return (
    <MainLayout>
      <Container className="pb-8">
        <Stack direction="vertical" spacing={6}>
          {/* Header */}
          <PageHeader
            title="Baltic Exchange"
            actionsInline
            actions={
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <SimpleSwitch
                  checked={showTCE}
                  onChange={setShowTCE}
                  inline
                  label={
                    <>
                      <span className="sm:hidden">{showTCE ? 'WS / Lump.' : 'TCE'}</span>
                      <span className="hidden sm:inline">
                        {showTCE ? 'Show WS/Lumpsum?' : 'Show TCE?'}
                      </span>
                    </>
                  }
                  className="shrink-0"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={refreshData}
                  disabled={loading}
                  title="Refresh data"
                  aria-label="Refresh data"
                  className="!min-h-9 !min-w-9 shrink-0 !px-2 !py-2"
                >
                  {loading ? <ProgressSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
              </div>
            }
          />
          {/* Error Alert */}
          {error && (
            <Alert variant="error" title="Error">
              {error}
            </Alert>
          )}
          {/* Loading State */}
          {loading && routes.length === 0 && <BalticSkeleton />}
          {/* Baltic Routes Accordion */}
          {routes.length > 0 && (
            <div className="space-y-2">
              {routes.map((route) => (
                <BalticAccordionItem
                  key={route.route}
                  route={route}
                  isExpanded={expandedRoute === route.route}
                  showTCE={showTCE}
                  selectedPeriod={selectedPeriod}
                  historyData={expandedRoute === route.route ? historyData : []}
                  historyLoading={historyLoading}
                  onToggle={() => {
                    const newExpandedRoute = expandedRoute === route.route ? null : route.route;
                    setExpandedRoute(newExpandedRoute);
                    if (newExpandedRoute) {
                      setSelectedRoute(route);
                      setSearchParams({ route: newExpandedRoute, period: selectedPeriod });
                    } else {
                      setSelectedRoute(null);
                      searchParams.delete('route');
                      setSearchParams(searchParams);
                    }
                  }}
                  onPeriodChange={handlePeriodChange}
                  periods={periods}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && routes.length === 0 && (
            <div className="py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No Baltic data available
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Baltic exchange data is currently not available. Please check your connection and
                try again.
              </p>
              <div className="mt-6">
                <Button onClick={refreshData} size="sm">
                  Retry
                </Button>
              </div>
            </div>
          )}
        </Stack>
      </Container>
    </MainLayout>
  );
}
